// SearchPage.js - FIXED: Use 'x-region' header consistently
import React, { useState, useEffect, useContext } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useRegion } from '../context/RegionContext';
import { AuthContext } from '../context/AuthContext';
import { auth } from '../firebaseConfig';
import './PageLayout.css';

function SearchPage() {
    const [searchResults, setSearchResults] = useState([]);
    const [filteredResults, setFilteredResults] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const location = useLocation();
    const navigate = useNavigate();
    
    // Filter states
    const [filters, setFilters] = useState({
        category: '',
        name: ''
    });
    
    // Dropdown options
    const [categoryOptions, setCategoryOptions] = useState([]);
    const [nameOptions, setNameOptions] = useState([]);

    // Use the global region context - no local region state needed
    const { selectedRegion } = useRegion();

    // ✅ single source of truth for role
    const { user } = useContext(AuthContext);
    const role = user?.role || 'user';
    const isAdmin = role === 'admin';
    const isUser  = role === 'user';
    const roleLoading = user === undefined; // when provider hasn't resolved yet

    // Get search query from URL
    const searchQuery = new URLSearchParams(location.search).get('query');

    useEffect(() => {
        if (searchQuery && selectedRegion) {
            setIsLoading(true);
            setError(null);

            const fetchSearchResults = async () => {
                try {
                    // ✅ Get Firebase token for authentication
                    const token = await auth.currentUser?.getIdToken();
                    
                    // ✅ FIXED: Use consistent 'x-region' header (not x-region-id)
                    const response = await axios.get(
                        `${process.env.REACT_APP_API_BASE_URL}/search?query=${encodeURIComponent(searchQuery)}`,
                        {
                            headers: {
                                'Authorization': `Bearer ${token}`,
                                'x-region': selectedRegion,  // ✅ FIXED: lowercase, no -id
                                'x-user-email': user?.email || '',
                                'x-user-role': role,
                            }
                        }
                    );

                    console.log("Search results:", response.data);
                    console.log("Using region:", selectedRegion);
                    const results = response.data;
                    setSearchResults(results);
                    setFilteredResults(results);
                    
                    // Extract unique options for dropdowns (prefer readable category name)
                    const categories = [...new Set(results.map(r => r.CategoryName || r.CategoryId))].filter(Boolean);
                    const names = [...new Set(results.map(r => r.QuantityUnitName))].filter(Boolean);
                    
                    setCategoryOptions(categories.sort());
                    setNameOptions(names.sort());
                    
                    // ✅ NEW: Log R-Values info for SearchPage
                    const withRValues = results.filter(r => r.RValue !== null && r.RValue !== undefined);
                    console.log(`📊 Search loaded ${results.length} results, ${withRValues.length} have R-Values`);
                    
                    setIsLoading(false);
                } catch (error) {
                    console.error("Error fetching search results:", error);
                    setError(error.response?.data?.message || "An error occurred while fetching search results.");
                    setIsLoading(false);
                }
            };

            fetchSearchResults();
        }
    }, [searchQuery, selectedRegion]); // Re-run when global region changes

    // Filter function
    useEffect(() => {
        let filtered = searchResults;

        if (filters.category) {
            filtered = filtered.filter(r => {
                const cat = r.CategoryName || r.CategoryId || '';
                return cat.toLowerCase().includes(filters.category.toLowerCase());
            });
        }

        if (filters.name) {
            filtered = filtered.filter(r => 
                r.QuantityUnitName && r.QuantityUnitName.toLowerCase().includes(filters.name.toLowerCase())
            );
        }

        setFilteredResults(filtered);
    }, [filters, searchResults]);

    const handleFilterChange = (filterType, value) => {
        setFilters({
            ...filters,
            [filterType]: value
        });
    };

    const clearFilters = () => {
        setFilters({
            category: '',
            name: ''
        });
    };

    const handleBreakdownClick = (quantityUnitId) => {
        console.log("Navigating to breakdown for QuantityUnitID:", quantityUnitId);
        navigate(`/breakdown/${quantityUnitId}`);
    };

    const handleAdminEdit = (result) => {
        navigate('/materialeditor', { 
            state: { 
                materialId: result.QuantityUnitId
            } 
        });
    };

    const handleUserEdit = (result) => {
        navigate('/composite-requests', {
            state: {
                prefill: {
                    materialId: result.QuantityUnitId,
                    materialName: result.QuantityUnitName || null,
                }
            },
        });
    };

    // Helper function to get column values based on global region
    const getRegionValue = (result, metric) => {
        const columnName = `${selectedRegion}_${metric}`;
        return result[columnName] || 'N/A';
    };

    // Helper function to format decimal places to 3
    const formatToThreeDecimals = (value) => {
        if (value === null || value === undefined || value === '' || value === 'N/A') return value;
        const num = parseFloat(value);
        return isNaN(num) ? value : num.toFixed(3);
    };

    // ✅ NEW: R-Value formatting function (same as BuildUpPage)
    const formatRValue = (value) => {
        if (value === null || value === undefined || value === '' || value === 'N/A') return '-';
        const num = parseFloat(value);
        return isNaN(num) ? '-' : num.toFixed(2);
    };

    // ✅ NEW: Get R-Value function (same as BuildUpPage logic)
    const getRValue = (result) => {
        // First try regional R-Value column (if it exists)
        const regional = result[`${selectedRegion}_RValue`];
        // Then fallback to generic RValue column
        return regional ?? result.RValue ?? null;
    };

    return (
        <div>
            {/* TOP BAR */}
            <div className="page-toolbar">
                <button className="back-btn" onClick={() => navigate(-1)}>← Back</button>
                <h1 className="page-title">Search Results for "{searchQuery}"</h1>
                <div className="page-actions" />
            </div>

            <div className="region-indicator">
                🌏 Current Region: <strong>{selectedRegion || 'Loading...'}</strong>
                <span className="role-indicator" style={{ marginLeft: 20 }}>
                    👤 Role: <strong>{roleLoading ? '...' : role.toUpperCase()}</strong>
                </span>
            </div>

            {/* FILTERS SECTION */}
            {searchResults.length > 0 && (
                <div className="filters-section">
                    <h3>Filters</h3>
                    <div className="filters-grid">
                        <div className="filter-group">
                            <label htmlFor="category-filter">Category</label>
                            <select
                                id="category-filter"
                                value={filters.category}
                                onChange={(e) => handleFilterChange('category', e.target.value)}
                                className="filter-select"
                            >
                                <option value="">All Categories</option>
                                {categoryOptions.map(category => (
                                    <option key={category} value={category}>
                                        {category}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="filter-group">
                            <label htmlFor="name-filter">Name</label>
                            <select
                                id="name-filter"
                                value={filters.name}
                                onChange={(e) => handleFilterChange('name', e.target.value)}
                                className="filter-select"
                            >
                                <option value="">All Names</option>
                                {nameOptions.map(name => (
                                    <option key={name} value={name}>
                                        {name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="filter-actions">
                            <button 
                                onClick={clearFilters}
                                className="clear-filters-btn"
                                disabled={!filters.category && !filters.name}
                            >
                                Clear Filters
                            </button>
                        </div>
                    </div>
                    
                    <div className="filter-results">
                        Showing {filteredResults.length} of {searchResults.length} items
                    </div>
                </div>
            )}
            
            <div>
                {isLoading ? (
                    <p>Loading search results...</p>
                ) : error ? (
                    <div className="alert alert--error">⚠️ {error}</div>
                ) : searchResults.length === 0 ? (
                    <div className="alert alert--info">
                        No results found for "{searchQuery}". Try searching for something else.
                    </div>
                ) : (
                    <div>
                        <table>
                            <thead>
                                <tr>
                                    <th>Category</th>
                                    <th>Name</th>
                                    <th>Description</th>
                                    <th>Unit</th>
                                    {isAdmin && <th>Rate ($)</th>}
                                    <th>Co2 (kg/Co2)</th>
                                    <th>Biogenic (kg/Co2)</th>
                                    <th>Weight (kg)</th>
                                    <th>R-Value</th> {/* ✅ R-Value column header */}
                                    <th>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredResults.map((result, index) => (
                                    <tr key={index}>
                                        <td>{result.CategoryName || result.CategoryId}</td>
                                        <td>
                                            {result.QuantityUnitName}
                                            {result.PrivateOwnerEmail && (
                                                <span style={{
                                                    marginLeft: 6, fontSize: 11, background: '#fef3c7',
                                                    color: '#92400e', padding: '1px 6px', borderRadius: 4,
                                                    fontWeight: 'bold',
                                                }}>
                                                    🔒 Private
                                                </span>
                                            )}
                                        </td>
                                        <td>{result.Description}</td>
                                        <td>{result.QuantityTypeName}</td>
                                        {isAdmin && <td>{formatToThreeDecimals(getRegionValue(result, 'Rate'))}</td>}
                                        <td>{formatToThreeDecimals(getRegionValue(result, 'Co2'))}</td>
                                        <td>{formatToThreeDecimals(getRegionValue(result, 'Co2_Biogenic'))}</td>
                                        <td>{formatToThreeDecimals(getRegionValue(result, 'Weight'))}</td>
                                        {/* ✅ NEW: R-Value cell with styling (same as BuildUpPage) */}
                                        <td className={getRValue(result) ? 'rvalue-cell has-value' : 'rvalue-cell no-value'}>
                                           {formatRValue(getRValue(result))}
                                        </td>
                                        <td className="action-cell">
                                            <div className="action-buttons">
                                                <button 
                                                    className="action-button action-button--breakdown"
                                                    onClick={() => handleBreakdownClick(result.QuantityUnitId)}
                                                >
                                                    Breakdown
                                                </button>

                                                {isAdmin && (
                                                    <button
                                                        className="action-button action-button--edit"
                                                        onClick={() => handleAdminEdit(result)}
                                                    >
                                                        Edit
                                                    </button>
                                                )}

                                                {isUser && (
                                                    <button
                                                        className="action-button action-button--edit"
                                                        onClick={() => handleUserEdit(result)}
                                                    >
                                                        Create Copy
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}

export default SearchPage;